'use client';
import React, { FC, useEffect, useRef } from 'react';
import { TypePressReleaseCollectionFields } from '@/core/generated-types';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import usePressReleaseCollection from '@/hooks/usePressReleaseCollection';
import { PressReleaseSnippet } from '@/components/common/PressReleaseSnippet';
import { BackgroundColorWrapper } from '@/components/common/BackgroundColorWrapper';
import { Loader } from '@/components/common/Loader';
import type { EntryFields } from 'contentful';
import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';

interface ArrowProps {
  classes?: string;
}

const Arrow: React.FC<ArrowProps> = ({ classes }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12.11 11.01"
      height="14"
      className={classes}
    >
      <g>
        <path
          d="M6.25.35 11.4 5.5l-5.15 5.15M11.4 5.5H0"
          style={{ fill: 'none', stroke: '#514f4e', strokeMiterlimit: 10 }}
        />
      </g>
    </svg>
  );
};

export const PressReleaseCollection: FC<TypePressReleaseCollectionFields> = ({
  entryName,
  heading = '',
  subtitle = '',
  backgroundColor,
  anchorId,
  localeOverride,
}) => {
  const {
    entries,
    status,
    truncatedPageNumbers,
    renderedPageNumbers,
    handlePageChange,
    totalPages,
    currentPage,
    hasInteracted,
  } = usePressReleaseCollection();

  const scrollTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasInteracted) return;
    if (!scrollTarget.current) return;

    scrollTarget.current.scrollIntoView({ behavior: 'smooth' });
  }, [currentPage, hasInteracted]);

  if (shouldHideWithLocaleOverride(localeOverride)) {
    return null;
  }

  return (
    <BackgroundColorWrapper color={backgroundColor?.fields?.color} applyGutters>
      <section
        data-testid={`press-release-collection-${entryName}`}
        className="section mx-auto max-w-[900px] px-4 py-10 lg:py-20"
        id={anchorId}
      >
        {heading && (
          <Heading
            tag={HeadingTag.H2}
            size={HeadingSize.H2}
            classes="container mx-auto mb-5 lg:mb-10"
            align="center"
            text={heading || ''}
          />
        )}
        {subtitle && (
          <div
            className="border-b border-solid border-gray-400 pb-2 text-base font-bold lg:text-lg"
            ref={scrollTarget}
          >
            {subtitle}
          </div>
        )}

        <div id="press-snippet-target">
          {status === 'loading' ? (
            <Loader
              loading={true}
              text=""
              className="mt-6 min-h-screen w-[200px]"
            />
          ) : (
            entries.map((entry) => {
              const { entryName, date, heading, richText } = entry.fields;

              return (
                <div key={entry.sys.id}>
                  <PressReleaseSnippet
                    entryName={entryName}
                    heading={heading}
                    date={date}
                    richText={richText as EntryFields.RichText}
                  />
                </div>
              );
            })
          )}
        </div>
        {truncatedPageNumbers.length > 1 && (
          <div className="mt-5 flex justify-center">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className={`mr-8 ${currentPage === 1 ? 'invisible' : ''}`}
              disabled={currentPage === 1}
            >
              <Arrow classes="transform scale-x-[-1]" />
            </button>
            <div id="press-numbers">{renderedPageNumbers}</div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className={`ml-8 ${
                currentPage === totalPages ? 'invisible' : ''
              }`}
              disabled={currentPage === totalPages}
            >
              <Arrow />
            </button>
          </div>
        )}
      </section>
    </BackgroundColorWrapper>
  );
};
